import request from '../utils/request';
import { UPLOAD_TIMEOUT_MS } from '../utils/uploadLimits';
import {
  NonStdAttachment,
  NonStdApplicableRule,
  NonStdChargeLine,
  NonStdChargeLinePayload,
  NonStdEntry,
  NonStdEntryPayload,
  NonStdFinalAttachment,
  NonStdLabel,
  NonStdLabelPayload,
  NonStdPackaging,
  NonStdDestroyInfo,
  NonStdDestroyPayload,
  NonStdDestroyRow,
  NonStdPlacementPayload,
  NonStdStageSnapshotItem,
  NonStdSummary,
  PriceHint,
  ContainerDraft,
  ContainersView,
  SettlementLine,
  SettlementOverview,
  SettlementPreview,
  SettlementUnitInfo,
} from '../types/nonstandardOps';

const BASE = '/api/v1/nonstandard-ops';

/** 汇总：条目、收费行、合计、轮次状态；按当前账号角色过滤（客户只看到可见内容，仓库员工看不到收费） */
export async function getNonStdSummary(instructionId: number): Promise<NonStdSummary> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/summary/`);
  return res.data;
}

export async function createNonStdEntry(instructionId: number, payload: NonStdEntryPayload): Promise<NonStdEntry> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/entries/`, payload);
  return res.data;
}

export async function updateNonStdEntry(entryId: number, payload: Partial<NonStdEntryPayload>): Promise<NonStdEntry> {
  const res = await request.patch(`${BASE}/entries/${entryId}/`, payload);
  return res.data;
}

export async function voidNonStdEntry(entryId: number, reason: string): Promise<void> {
  await request.delete(`${BASE}/entries/${entryId}/`, { data: { reason } });
}

/** 上传附件。客户上传（写回复）时必须带 instructionId */
export async function uploadNonStdAttachment(file: File, instructionId?: number): Promise<NonStdAttachment> {
  const form = new FormData();
  form.append('file', file);
  if (instructionId) form.append('instruction', String(instructionId));
  const res = await request.post(`${BASE}/upload/`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS });
  return res.data;
}

/** 提交本轮结果，请客户反馈：生成里程碑。哪些记录客户能看到由每条记录自己的"对客户可见"开关决定 */
export async function submitNonStdRound(instructionId: number, payload: { text?: string }): Promise<NonStdEntry> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/submit-round/`, payload);
  return res.data;
}

/** 客户回复 */
export async function replyNonStd(instructionId: number, payload: { text?: string; attachments?: NonStdAttachment[] }): Promise<NonStdEntry> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/customer-reply/`, payload);
  return res.data;
}

/** 指令列表用：这些指令里哪些正在等客户反馈 */
export async function getNonStdWaitingIds(ids: number[]): Promise<number[]> {
  if (ids.length === 0) return [];
  const res = await request.get(`${BASE}/waiting/`, { params: { ids: ids.join(',') } });
  return Array.isArray(res.data?.ids) ? res.data.ids : [];
}

export async function createNonStdChargeLine(instructionId: number, payload: NonStdChargeLinePayload): Promise<NonStdChargeLine> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/charge-lines/`, payload);
  return res.data;
}

export async function updateNonStdChargeLine(lineId: number, payload: NonStdChargeLinePayload): Promise<NonStdChargeLine> {
  const res = await request.patch(`${BASE}/charge-lines/${lineId}/`, payload);
  return res.data;
}

export async function voidNonStdChargeLine(lineId: number, reason: string): Promise<{ requires_recalc: boolean }> {
  const res = await request.delete(`${BASE}/charge-lines/${lineId}/`, { data: { reason } });
  return res.data;
}

export async function getNonStdPriceHint(params: { instruction: number; rule_id: number; quantity: string | number; tier_value?: string | number }): Promise<PriceHint> {
  const res = await request.get(`${BASE}/price-hint/`, { params });
  return res.data;
}

/** 本指令的项目此刻适用的收费规则（系统级/客户级/项目级，全部列出） */
export async function getNonStdApplicableRules(instructionId: number): Promise<NonStdApplicableRule[]> {
  const res = await request.get(`${BASE}/applicable-rules/`, { params: { instruction: instructionId } });
  return Array.isArray(res.data?.rules) ? res.data.rules : [];
}

// ---- 终审结果附件（管理员） ----
export async function listNonStdFinalAttachments(instructionId: number): Promise<NonStdFinalAttachment[]> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/final-attachments/`);
  return res.data;
}

export async function uploadNonStdFinalAttachment(instructionId: number, file: File): Promise<NonStdFinalAttachment[]> {
  const form = new FormData();
  form.append('file', file);
  const res = await request.post(`${BASE}/instructions/${instructionId}/final-attachments/`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS });
  return res.data;
}

export async function addNonStdFinalAttachmentsFromEntries(instructionId: number, picks: Array<{ entry_id: number; url: string }>): Promise<NonStdFinalAttachment[]> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/final-attachments/`, { entry_attachments: picks });
  return res.data;
}

export async function deleteNonStdFinalAttachment(instructionId: number, attachmentId: number): Promise<void> {
  await request.delete(`${BASE}/instructions/${instructionId}/final-attachments/${attachmentId}/`);
}

// ---- 容器标签（仅仓库员工/管理员） ----
export async function createNonStdLabel(instructionId: number, payload: NonStdLabelPayload): Promise<NonStdLabel> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/labels/`, payload);
  return res.data;
}

export async function updateNonStdLabel(labelId: number, payload: NonStdLabelPayload): Promise<NonStdLabel> {
  const res = await request.patch(`${BASE}/labels/${labelId}/`, payload);
  return res.data;
}

export async function voidNonStdLabel(labelId: number, reason: string): Promise<void> {
  await request.delete(`${BASE}/labels/${labelId}/`, { data: { reason } });
}

/** 批量修改标识组（group 为空 = 移出分组）；阶段 2 提交前才能改 */
export async function batchNonStdLabelGroup(instructionId: number, body: { label_ids: number[]; group: string }): Promise<NonStdLabel[]> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/labels/group/`, body);
  return res.data.labels;
}

/** 标记/取消"已消耗"（单个、选中的、整个组都用它，传标签编号）；skipped_unused：标记时被跳过的"未使用"容器数 */
export async function batchNonStdLabelConsume(
  instructionId: number,
  body: { label_ids: number[]; consumed: boolean },
): Promise<{ labels: NonStdLabel[]; skipped_unused: number }> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/labels/consume/`, body);
  return res.data;
}

/** 容器可以选的预设发货包材（选了会自动填入长宽高，重量要手填） */
export async function getNonStdPackagings(instructionId: number): Promise<NonStdPackaging[]> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/packagings/`);
  return res.data;
}

/** 生成标签 PDF（预览和打印共用）：已保存的标签传 label_ids，页面上还没保存的草稿传 items；copies 是每张的份数 */
export async function previewNonStdLabelsPdf(
  instructionId: number,
  body: { label_ids?: number[]; items?: NonStdLabelPayload[]; copies?: number },
): Promise<Blob> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/labels/preview/`, body, { responseType: 'blob' as any });
  return res.data as Blob;
}

/** 记录"已发送打印"（累加打印张数并留痕） */
export async function recordNonStdLabelsPrinted(
  instructionId: number,
  body: { label_ids: number[]; copies: number; channel: 'agent' | 'browser' },
): Promise<NonStdLabel[]> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/labels/printed/`, body);
  return res.data;
}

/** 请求用 blob 接收时，错误信息也是 blob：读出里面的 detail */
export async function blobErrorText(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  try {
    if (data instanceof Blob) {
      const parsed = JSON.parse(await data.text());
      return parsed?.detail || (parsed && typeof parsed === 'object' ? Object.values(parsed).flat().join('；') : '') || fallback;
    }
  } catch { /* 不是 JSON 就用兜底提示 */ }
  return data?.detail || err?.message || fallback;
}

// ---- "确认完成"阶段：容器核对和补充说明 ----
export async function updateNonStdLabelPlacement(labelId: number, payload: NonStdPlacementPayload): Promise<NonStdLabel> {
  const res = await request.patch(`${BASE}/labels/${labelId}/placement/`, payload);
  return res.data;
}

/** 批量：把选中的容器一起标记已贴标签，或设置同一个放置位置（已标记"未使用"的会被跳过） */
export async function batchNonStdLabelPlacement(
  instructionId: number,
  body: { label_ids: number[]; labeled?: boolean; location?: string },
): Promise<NonStdLabel[]> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/labels/placement/`, body);
  return res.data;
}

export async function createNonStdDestroyRecord(instructionId: number, payload: NonStdDestroyPayload): Promise<NonStdDestroyRow> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/destroy-records/`, payload);
  return res.data;
}

export async function updateNonStdDestroyRecord(recordId: number, payload: NonStdDestroyPayload): Promise<NonStdDestroyRow> {
  const res = await request.patch(`${BASE}/destroy-records/${recordId}/`, payload);
  return res.data;
}

export async function voidNonStdDestroyRecord(recordId: number, reason?: string): Promise<void> {
  await request.delete(`${BASE}/destroy-records/${recordId}/`, { data: { reason: reason || '' } });
}

/** 勾选/取消"本指令没有发生销毁" */
export async function setNonStdNoDestroy(instructionId: number, declared: boolean): Promise<NonStdDestroyInfo> {
  const res = await request.put(`${BASE}/instructions/${instructionId}/no-destroy/`, { declared });
  return res.data;
}

export async function saveNonStdConfirmNote(instructionId: number, text: string): Promise<{ text: string; updated_at: string }> {
  const res = await request.put(`${BASE}/instructions/${instructionId}/confirm-note/`, { text });
  return res.data;
}

// ---- 终审页：各阶段快照列表（仅仓库员工/管理员） ----
export async function getNonStdStageSnapshots(instructionId: number): Promise<NonStdStageSnapshotItem[]> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/stage-snapshots/`);
  return res.data;
}


// ---- 终审库存结算（仅管理员） ----
export async function getNonStdSettlement(instructionId: number): Promise<SettlementOverview> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/settlement/`);
  return res.data;
}

/** 整体保存草稿（替换所有转换行）；version 是上次读到的版本，被别人改过会返回 409 */
export async function saveNonStdSettlement(instructionId: number, body: { version: number; lines: SettlementLine[] }): Promise<SettlementOverview> {
  const res = await request.put(`${BASE}/instructions/${instructionId}/settlement/`, body);
  return res.data;
}

export async function previewNonStdSettlement(instructionId: number): Promise<SettlementPreview> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/settlement/preview/`, {});
  return res.data;
}

export async function confirmNonStdSettlement(
  instructionId: number,
  body: { fingerprint: string; container_fingerprint?: string; acknowledged: boolean; note?: string },
): Promise<SettlementOverview> {
  const res = await request.post(`${BASE}/instructions/${instructionId}/settlement/confirm/`, body);
  return res.data;
}

/** 选输出库存单元用的搜索（只在本指令涉及的项目里找） */
export async function searchNonStdSettlementUnits(instructionId: number, q: string): Promise<SettlementUnitInfo[]> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/settlement/units/`, { params: { q } });
  return res.data;
}

export async function getNonStdContainers(instructionId: number): Promise<ContainersView> {
  const res = await request.get(`${BASE}/instructions/${instructionId}/settlement/containers/`);
  return res.data;
}

export async function saveNonStdContainers(instructionId: number, body: { version: number; containers: ContainerDraft[] }): Promise<ContainersView> {
  const res = await request.put(`${BASE}/instructions/${instructionId}/settlement/containers/`, body);
  return res.data;
}
